import React, { useState, ReactEventHandler, useCallback, useMemo, useRef } from "react";
import { FadeInOnLoadMedia } from "@novel/shared/components/FadeInOnLoadMedia";
type LongerDimension = "vertical" | "horizontal";
interface CappedDimensionMediaProps {
  readonly className?: string;
  readonly calculation?: "cover" | "contain";
  readonly onLoad?: (e: any, direction: LongerDimension) => void;
  readonly onError?: (e: any) => void;
  readonly src?: string;
  readonly alt?: string;
  readonly style?: React.CSSProperties;
  readonly type?: "image" | "video";
  // video only
  readonly poster?: string;
  readonly controls?: boolean;
  readonly muted?: boolean;
  readonly loop?: boolean;
  readonly autoPlay?: boolean;
}
export function CappedDimensionMedia({
  className,
  calculation = "contain",
  src,
  alt,
  onLoad,
  onError,
  style,
  type = "image",
  poster,
  controls,
  muted,
  loop,
  autoPlay
}: CappedDimensionMediaProps): JSX.Element {
  const [, setIsLoadedState] = useState(false);
  const [direction, setDirection] = useState<LongerDimension>("horizontal");
  const [overrideAsContain, setOverrideAsContain] = useState<boolean>(false);
  const elementRef = useRef<HTMLVideoElement | HTMLImageElement>();
  const setLoadedCb: ReactEventHandler<HTMLImageElement> = useCallback(e => {
    const containingElement = elementRef?.current?.parentElement;
    let containerHeightToWidth = 1;
    if (containingElement) {
      // client height excludes borders (vs. offsetHeight)
      // we want the inner dimensions so this is correct
      containerHeightToWidth = containingElement.clientHeight / containingElement.clientWidth;
    }
    const heightToWidth = e.currentTarget.naturalHeight / e.currentTarget.naturalWidth;
    const calculatedDirection = heightToWidth > containerHeightToWidth ? "vertical" : "horizontal";
    if (heightToWidth > containerHeightToWidth) {
      setDirection(calculatedDirection);
    }
    // if ratio is too far off then cover looks strange
    if (heightToWidth > containerHeightToWidth * 2 || heightToWidth < containerHeightToWidth / 2) {
      setOverrideAsContain(true);
    }
    setIsLoadedState(true);
    if (onLoad) {
      onLoad(e, calculatedDirection);
    }
  }, [onLoad]);
  const setHadErrorCb = useCallback((e: any) => {
    if (onError) {
      onError(e);
    }
  }, [onError]);
  const calculationIsContain = calculation === "contain" || overrideAsContain;
  const finalDirection = calculationIsContain && direction === "horizontal" || !calculationIsContain && direction === "vertical" ? "horizontal" : "vertical";
  const finalStyleObj = useMemo(() => finalDirection === "horizontal" ? {
    minHeight: 0,
    minWidth: "100%",
    ...style
  } : {
    minHeight: "100%",
    minWidth: 0,
    ...style
  }, [style, finalDirection]);
  return <FadeInOnLoadMedia ref={(elementRef as any)} src={src} alt={alt} controls={controls} type={type} muted={muted} loop={loop} autoPlay={autoPlay} poster={poster} onLoad={setLoadedCb} onError={setHadErrorCb} style={finalStyleObj} className={className} {...finalDirection === "horizontal" ? {
    width: "100%"
  } : {
    height: "100%"
  }} />;
}